/**
 * Adapter layer: normalizes any provider payload into the UI Movie shape.
 * Components only ever consume the output of these functions.
 */
const str = (v, max = 4000) => (typeof v === "string" ? v.trim().slice(0, max) : "");
const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
/** Uploaded media is served by our API (/api/media/:id); external URLs pass through. */
export const mediaUrl = (v) => (typeof v === "string" && /^\/api\/media\/[a-f0-9]+$/.test(v.trim()) ? BACKEND + v.trim() : v);
const url = (v) => {
  if (typeof v !== "string") return "";
  const t = v.trim();
  if (/^\/api\/media\/[a-f0-9]+$/.test(t)) return BACKEND + t;
  if (/^\/api\/stream\/[\w-]+\/[\w-]+$/.test(t)) return BACKEND + t; // proxied video (Range-aware)
  return /^https?:\/\//i.test(t) ? t : "";
};

export function adaptMovie(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = str(raw.id || raw.video_id, 64);
  if (!id) return null;
  const quality = Array.isArray(raw.quality) ? raw.quality : raw.quality ? [raw.quality] : [];
  return {
    id,
    type: raw.type === "series" ? "series" : "movie",
    title: str(raw.title, 200) || "Nomsiz film",
    originalTitle: str(raw.original_title, 200),
    description: str(raw.description),
    year: num(raw.year),
    duration: num(raw.duration),
    rating: num(raw.rating),
    ageRating: str(raw.age_rating, 8),
    country: str(raw.country, 60),
    language: str(raw.language, 60),
    director: str(raw.director, 120),
    genres: Array.isArray(raw.genres)
      ? raw.genres.map((g) => (typeof g === "string" ? { slug: g, name: g } : { slug: str(g.slug, 40), name: str(g.name, 40) })).filter((g) => g.slug)
      : [],
    quality: quality.map((q) => str(String(q), 10)).filter(Boolean).slice(0, 4),
    poster: url(raw.poster || raw.image),
    backdrop: url(raw.backdrop),
    trailerUrl: url(raw.trailer_url),
    cast: Array.isArray(raw.cast) ? raw.cast.map((c) => ({ name: str(c?.name, 80), role: str(c?.role, 80), photo: url(c?.photo) })).filter((c) => c.name) : [],
    featured: Boolean(raw.featured),
    hasVideo: raw.has_video !== false,
    views: num(raw.views) || 0,
    source: raw.source || "local",
    progress: raw.progress ? { position: num(raw.progress.position) || 0, duration: num(raw.progress.duration) || 0, episodeId: raw.progress.episode_id || null } : null,
    seasons: Array.isArray(raw.seasons)
      ? raw.seasons.map((s) => ({
          number: num(s.number) ?? 1,
          title: str(s.title, 120),
          episodes: (s.episodes || []).map((e) => ({ id: str(e.id, 40), number: num(e.number) || 1, title: str(e.title, 200), description: str(e.description, 2000), duration: num(e.duration), still: url(e.still), hasVideo: Boolean(e.has_video) })).filter((e) => e.id),
        }))
      : [],
  };
}

export const adaptList = (arr) => (Array.isArray(arr) ? arr.map(adaptMovie).filter(Boolean) : []);

export function adaptPage(res) {
  return {
    items: adaptList(res?.items),
    page: res?.page || 1,
    total: res?.total || 0,
    hasMore: Boolean(res?.has_more),
  };
}

export function adaptSources(res) {
  const list = Array.isArray(res?.sources) ? res.sources : [];
  return list
    .map((s) => ({ label: str(s.label, 20) || "Auto", url: url(s.url), type: s.type === "hls" || /\.m3u8(\?|$)/i.test(s.url || "") ? "hls" : "mp4" }))
    .filter((s) => s.url);
}
